import React, { useCallback } from 'react';

import { FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { AddIcon } from 'src/components/icons/AddIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    flexShrink: 0,
  },
  actionButtons: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
  },
  numberInput: {
    flex: 1.5,
    minWidth: 0,
  },
  offsetRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  select: {
    flex: 2,
    minWidth: 0,
  },
}));

export interface NotificationOffset {
  amount: number;
  position: Apollo.RelativeTimePosition;
  unit: Apollo.RelativeTimeUnit;
}

interface NotificationOffsetBuilderProps {
  offsets: NotificationOffset[];
  onChange: (offsets: NotificationOffset[]) => void;
}

export const defaultOffset: NotificationOffset = {
  amount: 1,
  position: Apollo.RelativeTimePosition.Before,
  unit: Apollo.RelativeTimeUnit.Days,
};

const VALID_UNITS = new Set<string>(Object.values(Apollo.RelativeTimeUnit));
const VALID_POSITIONS = new Set<string>(Object.values(Apollo.RelativeTimePosition));

function getIsValidUnit(value: string): value is Apollo.RelativeTimeUnit {
  return VALID_UNITS.has(value);
}

function getIsValidPosition(value: string): value is Apollo.RelativeTimePosition {
  return VALID_POSITIONS.has(value);
}

export const NotificationOffsetBuilder = React.memo(function NotificationOffsetBuilder({
  offsets,
  onChange,
}: NotificationOffsetBuilderProps): JSX.Element {
  const { classes } = useStyles();

  const handleAddOffset = useCallback(() => {
    onChange([...offsets, { ...defaultOffset }]);
  }, [offsets, onChange]);

  const handleRemoveOffset = useCallback(
    (index: number) => {
      onChange(offsets.filter((_, i) => i !== index));
    },
    [offsets, onChange]
  );

  const handleUpdateOffset = useCallback(
    (index: number, updates: Partial<NotificationOffset>) => {
      onChange(offsets.map((offset, i) => (i === index ? { ...offset, ...updates } : offset)));
    },
    [offsets, onChange]
  );

  return (
    <div className={classes.container}>
      {offsets.map((offset, index) => {
        const isLast = index === offsets.length - 1;

        return (
          <div key={index} className={classes.offsetRow}>
            <TextField
              className={classes.numberInput}
              size="small"
              slotProps={{
                htmlInput: { min: 1 },
              }}
              type="number"
              value={offset.amount > 0 ? offset.amount : ''}
              onBlur={() => {
                if (offset.amount < 1) handleUpdateOffset(index, { amount: 1 });
              }}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') return handleUpdateOffset(index, { amount: 0 });
                const value = parseInt(raw, 10);
                if (!isNaN(value) && value >= 0) handleUpdateOffset(index, { amount: value });
              }}
            />
            <FormControl className={classes.select} size="small">
              <Select<Apollo.RelativeTimeUnit>
                value={offset.unit}
                onChange={e => {
                  const val = e.target.value;
                  if (getIsValidUnit(val)) handleUpdateOffset(index, { unit: val });
                }}
              >
                <MenuItem value={Apollo.RelativeTimeUnit.Days}>Day</MenuItem>
                <MenuItem value={Apollo.RelativeTimeUnit.Weeks}>Week</MenuItem>
                <MenuItem value={Apollo.RelativeTimeUnit.Months}>Month</MenuItem>
              </Select>
            </FormControl>
            <FormControl className={classes.select} size="small">
              <Select<Apollo.RelativeTimePosition>
                value={offset.position}
                onChange={e => {
                  const val = e.target.value;
                  if (getIsValidPosition(val)) handleUpdateOffset(index, { position: val });
                }}
              >
                <MenuItem value={Apollo.RelativeTimePosition.Before}>Before</MenuItem>
                <MenuItem value={Apollo.RelativeTimePosition.After}>After</MenuItem>
              </Select>
            </FormControl>
            <div className={classes.actionButtons}>
              {offsets.length > 1 && (
                <IconButton
                  className={classes.actionButton}
                  size="small"
                  onClick={() => handleRemoveOffset(index)}
                >
                  <TrashcanIcon />
                </IconButton>
              )}
              {isLast && (
                <IconButton className={classes.actionButton} size="small" onClick={handleAddOffset}>
                  <AddIcon />
                </IconButton>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});
